import React, { Fragment, useState } from 'react';
import { Icon } from 'selfly-ui-react';
import { Row, Col } from 'reactstrap';
import { groupBy } from 'lodash';

import AnalyticsSearchBar from './../wasteDashboard/analyticsSearchBar';
import TabMenu from './tabMenu';
import DiscountDetails from './discountDetails';
import { chartColors } from '../cubeCharts';
import DiscountAnalyticsExport from './discountExport';
import { toCSVString, toExcelObject } from '../../../export';

const DiscountList = ({ discountProducts, t }) => {
    const [activeRow, setActiveRow] = useState(null);
    const [activeTabMenu, setActiveTabMenu] = useState('products');

    const allDiscountProductsList = [];

    const discountRules = Object.keys(discountProducts);
    discountRules.forEach(rule => {
        const discountRuleStops = Object.keys(discountProducts[rule]);
        discountRuleStops.forEach(stop => {
            let totalSoldCounts = 0,
                discountAmount = 0,
                currency,
                normalSalesAmount = 0,
                discountSalesAmount = 0,
                discountAmountType = null;
            const purchasedProducts = [];
            if (!discountAmount) {
                discountAmount = parseFloat(stop);
            }
            const products = discountProducts[rule][stop];
            products.forEach(product => {
                discountAmountType = 'percentage';
                let originalUnitPrice = 0;
                if (product['Transactions.discountAmountType']) {
                    discountAmountType = product['Transactions.discountAmountType'];
                }

                if (!product['Transactions.originalPrice']) {
                    if (discountAmountType === 'percentage') {
                        originalUnitPrice = (100 * product['Transactions.oneUnitPrice']) / (100 - product['Transactions.discountAmount']);
                    }
                } else {
                    originalUnitPrice = product['Transactions.originalPrice'];
                }
                // handle divide by 0 case
                originalUnitPrice = isFinite(originalUnitPrice) ? originalUnitPrice : 0;

                purchasedProducts.push(product);
                currency = product['Transactions.currency'];
                totalSoldCounts = totalSoldCounts + parseInt(product['Transactions.soldCount']);
                normalSalesAmount = parseFloat(normalSalesAmount) + originalUnitPrice * parseInt(product['Transactions.soldCount']);
                discountSalesAmount =
                    parseFloat(discountSalesAmount) + parseFloat(product['Transactions.oneUnitPrice']) * parseInt(product['Transactions.soldCount']);
            });

            const discountData = {
                discountName: rule,
                discountAmount,
                totalSoldCounts,
                normalSalesAmount,
                discountSalesAmount,
                currency,
                salesDifference: normalSalesAmount - discountSalesAmount,
                products: groupBy(purchasedProducts, 'Transactions.productName'),
                cabinets: groupBy(purchasedProducts, 'Device.deviceName'),
                locations: groupBy(purchasedProducts, 'Transactions.locationName'),
                discountAmountType,
            };

            allDiscountProductsList.push(discountData);
        });
    });

    const getCSVHeader = type => {
        const headerCommon = [
            'discount_name',
            'discount_amount',
            'discount_type',
            'discount_currency',
            'discount_sales_count',
            'discount_total_amount',
            'discount_sales_sum',
        ];

        const headerForType =
            {
                products: [
                    'product_name',
                    'product_image_url',
                    'product_barcode',
                    'product_sales_count',
                    'product_normal_price',
                    'product_discount_price',
                    'product_discount_sales_sum',
                ],
                locations: ['location_name', 'location_sales_count', 'location_discount_sales_sum'],
                cabinets: ['cabinet_name', 'cabinet_sales_count', 'cabinet_discount_sales_sum'],
            }[type] ?? [];

        return headerCommon.concat(headerForType);
    };

    const generateCSVObject = type => {
        function getDiscountPart(discount) {
            return [
                discount.discountName,
                discount.discountAmount,
                discount.discountAmountType,
                discount.currency,
                discount.totalSoldCounts,
                (discount.normalSalesAmount - discount.discountSalesAmount).toFixed(2),
                discount.discountSalesAmount.toFixed(2),
            ];
        }

        const csvLines = [];
        switch (type) {
            case 'products': {
                for (const discount of allDiscountProductsList) {
                    const discountPart = getDiscountPart(discount);
                    for (const [productName, transactions] of Object.entries(discount.products)) {
                        const transaction_0 = transactions[0];
                        if (transaction_0) {
                            // should be always true, array is the result of group_by
                            const imageUrl = transaction_0['Transactions.imageUrl'];
                            const barcode = transaction_0['Transactions.barcode'];
                            const discountPrice = parseFloat(transaction_0['Transactions.oneUnitPrice']);
                            const discountAmount = parseFloat(transaction_0['Transactions.discountAmount']);
                            const discountAmountType = transaction_0['Transactions.discountAmountType'] ?? 'percentage';

                            let originalUnitPrice = parseFloat(transaction_0['Transactions.originalPrice']);
                            if (originalUnitPrice === undefined || originalUnitPrice === null) {
                                if (discountAmountType === 'percentage') {
                                    originalUnitPrice =
                                        discountAmount === 100 // handle divide by zero
                                            ? 0
                                            : (100 * discountPrice) / (100 - discountAmount);
                                } else {
                                    // if (discountAmountType === 'fixed') {
                                    originalUnitPrice = discountPrice + discountAmount;
                                }
                            }

                            let productSalesCount = 0,
                                productSalesAmount = 0;
                            for (const transaction of transactions) {
                                const soldCount = parseInt(transaction['Transactions.soldCount']);
                                productSalesCount += soldCount;
                                productSalesAmount += soldCount * discountPrice;
                            }

                            const productPart = [productName, imageUrl, barcode, productSalesCount, originalUnitPrice, discountPrice, productSalesAmount];
                            csvLines.push(discountPart.concat(productPart));
                        }
                    }
                }

                break;
            }
            case 'locations': {
                for (const discount of allDiscountProductsList) {
                    const discountPart = getDiscountPart(discount);
                    for (const [locationName, transactions] of Object.entries(discount.locations)) {
                        const transaction_0 = transactions[0];
                        if (transaction_0) {
                            // should be always true, array is the result of group_by
                            const discountPrice = parseFloat(transaction_0['Transactions.oneUnitPrice']);

                            let locationSalesCount = 0,
                                locationSalesAmount = 0;
                            for (const transaction of transactions) {
                                const soldCount = parseInt(transaction['Transactions.soldCount']);
                                locationSalesCount += soldCount;
                                locationSalesAmount += soldCount * discountPrice;
                            }

                            const locationPart = [locationName, locationSalesCount, locationSalesAmount];
                            csvLines.push(discountPart.concat(locationPart));
                        }
                    }
                }

                break;
            }
            case 'cabinets': {
                for (const discount of allDiscountProductsList) {
                    const discountPart = getDiscountPart(discount);
                    for (const [cabinetName, transactions] of Object.entries(discount.cabinets)) {
                        const transaction_0 = transactions[0];
                        if (transaction_0) {
                            // should be always true, array is the result of group_by
                            const discountPrice = parseFloat(transaction_0['Transactions.oneUnitPrice']);

                            let cabinetSalesCount = 0,
                                cabinetSalesAmount = 0;
                            for (const transaction of transactions) {
                                const soldCount = parseInt(transaction['Transactions.soldCount']);
                                cabinetSalesCount += soldCount;
                                cabinetSalesAmount += soldCount * discountPrice;
                            }

                            const cabinetPart = [cabinetName, cabinetSalesCount, cabinetSalesAmount];
                            csvLines.push(discountPart.concat(cabinetPart));
                        }
                    }
                }

                break;
            }
            default:
            // nothing
        }

        return {
            header: getCSVHeader(type),
            lines: csvLines,
        };
    };

    const generateCSVBlob = type => {
        const csvObject = generateCSVObject(type);

        const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const csvContentType = isSafari() ? 'application/csv' : 'text/csv';
        const csvString = toCSVString(csvObject);
        return new Blob([csvString], { type: csvContentType });
    };

    const generateExcelObject = type => {
        const csvObject = generateCSVObject(type);

        return toExcelObject(csvObject);
    };

    const headers = [
        t('name'),
        t('discounts'),
        t('salesCount'),
        /* t('normalSalesAmount'), hide for now*/
        t('discountAmount'),
        t('discountSalesAmount'),
    ];

    return (
        <Fragment>
            <Row className="ml-4 mr-2 py-2">
                <Col className="pl-0 pr-0">
                    <div style={{ float: 'right' }}>
                        <DiscountAnalyticsExport t={t} generateExcelObject={generateExcelObject} generateCSVBlob={generateCSVBlob} />
                    </div>
                </Col>
            </Row>
            <Row className="ml-4 mr-2">
                <table className="se-table discount-table">
                    <thead className="se-table-head">
                        <tr>
                            <th style={{ width: '50px', paddingLeft: '15px' }}>#</th>
                            {headers.map(header => (
                                <th key={header}>
                                    <div className="se-table-head-content discount-table-header">{header}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="discount-table-body">
                        {allDiscountProductsList.length === 0 && (
                            <tr className="discount-table-no-data">
                                <td colSpan={headers.length + 1}>{t('noData')}</td>
                            </tr>
                        )}
                        {Object.values(allDiscountProductsList).map((discount, index) => (
                            <Fragment key={discount.discountName + discount.discountAmount}>
                                <tr
                                    className={`se-table-item se-table-item--clickable ${index === activeRow ? 'active-row' : ''}`}
                                    onClick={() => {
                                        activeRow === index ? setActiveRow(null) : setActiveRow(index);
                                    }}
                                >
                                    <td style={{ width: '50px', paddingLeft: '15px' }}>{index + 1}</td>
                                    <td>
                                        <span className="discount-table-rule"> {discount.discountName} </span>{' '}
                                        <Icon color="blue">{activeRow === index ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>
                                    </td>
                                    <td>
                                        {discount.discountAmountType === 'percentage'
                                            ? discount.discountAmount + '%'
                                            : discount.discountAmountType === 'fixed'
                                                ? '- ' + discount.discountAmount.toFixed(2) + discount.currency
                                                : t('freeDiscount')}{' '}
                                    </td>
                                    <td>{discount.totalSoldCounts}</td>
                                    {/* hide for now
                                <td>
                                    {discount.normalSalesAmount.toFixed(2)}
                                    {discount.currency}
                                </td>
                                */}
                                    <td>
                                        {discount.currency}
                                        {(discount.normalSalesAmount - discount.discountSalesAmount).toFixed(2)}
                                    </td>
                                    <td>
                                        <span
                                            className="discount-table-balance"
                                            style={{
                                                background: `${chartColors[index % chartColors.length]}`,
                                            }}
                                        >
                                            {discount.currency}
                                            {discount.discountSalesAmount.toFixed(2)}
                                        </span>
                                    </td>
                                </tr>
                                <tr className={index === activeRow ? 'active-row-detail--visible' : 'active-row-detail--hidden'}>
                                    <td colSpan="6" style={{ background: '#fff' }}>
                                        <TabMenu activeMenu={menu => setActiveTabMenu(menu)} active={activeTabMenu} t={t} />
                                        <Row className="ml-2">
                                            <AnalyticsSearchBar />
                                        </Row>
                                        <DiscountDetails detailType={activeTabMenu} discountProducts={discount} activeRow={activeRow} t={t} />
                                    </td>
                                </tr>
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </Row>
        </Fragment>
    );
};

export default DiscountList;
